import { useCallback, useMemo, useRef } from "react";
import { useSpring } from "@react-spring/web";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export function useTilt(options = {}) {
  const {
    max = 12,
    scale = 1.03,
    perspective = 1200,
    config = { mass: 1, tension: 280, friction: 26 },
  } = options;
  const ref = useRef(null);
  const [springs, api] = useSpring(() => ({
    xys: [0, 0, 1],
    glow: 0,
    config,
  }));

  const handlePointerMove = useCallback(
    (event) => {
      const element = ref.current;
      if (!element) return;
      const rect = element.getBoundingClientRect();
      const x = clamp((event.clientX - rect.left) / rect.width, 0, 1);
      const y = clamp((event.clientY - rect.top) / rect.height, 0, 1);
      const tiltX = (y - 0.5) * -2 * max;
      const tiltY = (x - 0.5) * 2 * max;
      api.start({ xys: [tiltX, tiltY, scale], glow: 1 });
    },
    [api, max, scale],
  );

  const handlePointerLeave = useCallback(() => {
    api.start({ xys: [0, 0, 1], glow: 0 });
  }, [api]);

  const style = useMemo(
    () => ({
      transform: springs.xys.to(
        (x, y, s) =>
          `perspective(${perspective}px) rotateX(${x}deg) rotateY(${y}deg) scale(${s})`,
      ),
      transformStyle: "preserve-3d",
    }),
    [springs.xys, perspective],
  );

  return {
    ref,
    style,
    glow: springs.glow,
    bind: {
      onPointerMove: handlePointerMove,
      onPointerLeave: handlePointerLeave,
    },
  };
}
